/**
 * Component: AppRoutes
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01, FRONTEND-WEB-V1-R11, FRONTEND-WEB-V1-R12,
 *               SEARCH-SEMANTIC-V1-R01, SEARCH-SIMPLE-V1-R01, SEARCH-HYBRID-V1-R02,
 *               GALEN-CHAT-V1-R01, ADMIN-V1-R07, AUTH-V1-R15,
 *               BROWSE-V1-R03, BROWSE-V1-R04
 *
 * Application routing configuration with feature flag support.
 * Provides routes for all four primary interaction modes plus BROWSE-V1.
 * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules via configuration feature flags.
 * FRONTEND-WEB-V1-R12: Disabled modules redirect to home instead of showing content.
 * BROWSE-V1-R03: /browse list route wrapped in FeatureRoute so the list
 *                page is only reachable when flags.browse is true.
 * BROWSE-V1-R04: /browse/guidelines/:guidelineId viewer route wrapped
 *                in the same FeatureRoute gate so a direct URL hit with
 *                browse disabled redirects home instead of rendering.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { HomePage } from '@/features/home/HomePage';
import { NotFoundPage } from '@/features/error/NotFoundPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignInTokenPage } from '@/features/auth/SignInTokenPage';
import { SemanticSearchPage } from '@/features/search/SemanticSearchPage';
import { SimpleSearchPage } from '@/features/search/SimpleSearchPage';
import { GalenSearchPage } from '@/features/search/GalenSearchPage';
import { ChatPage } from '@/features/chat/ChatPage';
import { BrowsePage, ViewerPage } from '@/features/browse';
import { ConfigurationPage } from '@/features/admin/ConfigurationPage';
import { WidgetConfigPage } from '@/features/admin/WidgetConfigPage';
import { SpeechConfigPage } from '@/features/admin/SpeechConfigPage';
import { UsageDashboardPage } from '@/features/admin/UsageDashboardPage';
import { UserManagementPage } from '@/features/admin/UserManagementPage';
import { ResourceAccessLogPage } from '@/features/admin/ResourceAccessLogPage';
import { SafetyValidationLogPage } from '@/features/admin/SafetyValidationLogPage';
import { QueryLogPage } from '@/features/admin/QueryLogPage';
import { PerformanceMonitoringPage } from '@/features/admin/PerformanceMonitoringPage';
import { ReportingPage } from '@/features/admin/ReportingPage';
import { useFeatureFlags } from '@/contexts';
import type { FeatureFlags } from '@/types';

/**
 * Protected route wrapper that redirects to home if feature is disabled.
 * FRONTEND-WEB-V1-R11: Runtime toggling of UI modules.
 * FRONTEND-WEB-V1-R12: Fully hide disabled modules (redirect instead of render).
 */
interface FeatureRouteProps {
  featureKey: keyof FeatureFlags;
  children: React.ReactElement;
}

function FeatureRoute({ featureKey, children }: FeatureRouteProps): React.ReactElement {
  const { flags, isLoading } = useFeatureFlags();

  // During loading, render null to avoid flash
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[200px]" aria-label="Loading" />;
  }

  // If feature is disabled, redirect to home
  if (!flags[featureKey]) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export function AppRoutes(): React.ReactElement {
  return (
    <Routes>
      {/* Auth routes - outside Layout (no header/footer) */}
      <Route path="login" element={<LoginPage />} />
      {/* SAML SSO token handoff route (AUTH-V1-R02) */}
      <Route path="sign-in-token/:token" element={<SignInTokenPage />} />

      {/* Main app routes - with Layout (AUTH-V1-R06: Protected routes) */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<HomePage />} />

        {/* FRONTEND-WEB-V1-R01: Four primary interaction modes */}
        {/* FRONTEND-WEB-V1-R11: Each route wrapped with FeatureRoute for flag-based access */}

        {/* Chat routes (GALEN-CHAT-V1-R01, GALEN-CHAT-V1-R08) */}
        <Route
          path="chat/:conversationId?"
          element={
            <FeatureRoute featureKey="chat">
              <ChatPage />
            </FeatureRoute>
          }
        />

        {/* Galen Search routes (SEARCH-HYBRID-V1-R02) */}
        <Route
          path="search/hybrid"
          element={
            <FeatureRoute featureKey="galenSearch">
              <GalenSearchPage />
            </FeatureRoute>
          }
        />

        {/* Semantic Search routes (SEARCH-SEMANTIC-V1-R01) */}
        <Route
          path="search/semantic"
          element={
            <FeatureRoute featureKey="semanticSearch">
              <SemanticSearchPage />
            </FeatureRoute>
          }
        />

        {/* Simple Search routes (SEARCH-SIMPLE-V1-R01) */}
        <Route
          path="search/simple"
          element={
            <FeatureRoute featureKey="simpleSearch">
              <SimpleSearchPage />
            </FeatureRoute>
          }
        />

        {/* Browse routes (BROWSE-V1-R03 list, BROWSE-V1-R04 viewer) -
            both gated on flags.browse, which mirrors the deployment-time
            browse_enabled setting. */}
        <Route
          path="browse"
          element={
            <FeatureRoute featureKey="browse">
              <BrowsePage />
            </FeatureRoute>
          }
        />
        <Route
          path="browse/guidelines/:guidelineId"
          element={
            <FeatureRoute featureKey="browse">
              <ViewerPage />
            </FeatureRoute>
          }
        />

        {/* Admin routes (ADMIN-V1-R07, AUTH-V1-R06: admin role required) */}
        <Route
          path="admin/usage"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <UsageDashboardPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Admin configuration route (CONFIG-V1-R15, CONFIG-V1-R17) */}
        <Route
          path="admin/configuration"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <ConfigurationPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Widget configuration admin route (ADMIN-V1-R16, CONFIG-V1-R18) */}
        <Route
          path="admin/widget-config"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <WidgetConfigPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Speech configuration admin route: languages (SPEECH-V1-R16),
            phrase list and normalisation rules (SPEECH-V1-R12). */}
        <Route
          path="admin/speech-config"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <SpeechConfigPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />
        {/* Redirect the previous path so an existing bookmark still lands
            on the page rather than a blank 404. */}
        <Route
          path="admin/speech-phrase-config"
          element={<Navigate to="/admin/speech-config" replace />}
        />

        {/* Admin user management route (AUTH-V1-R15) */}
        <Route
          path="admin/users"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <UserManagementPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Admin resource access logs route (ADMIN-V1-R06) */}
        <Route
          path="admin/logs/resources"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <ResourceAccessLogPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Admin safety validation logs route (ADMIN-V1-R04) */}
        <Route
          path="admin/logs/safety"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <SafetyValidationLogPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Admin query logs route (ADMIN-V1-R02) */}
        <Route
          path="admin/logs/queries"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <QueryLogPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Admin performance monitoring route (ADMIN-V1-R12) */}
        <Route
          path="admin/performance"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <PerformanceMonitoringPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        {/* Admin reporting route (REPORTING-V1-R12) */}
        <Route
          path="admin/reporting"
          element={
            <ProtectedRoute requiredRoles={['admin']}>
              <FeatureRoute featureKey="admin">
                <ReportingPage />
              </FeatureRoute>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
